import { InitiateAuthCommand } from '@aws-sdk/client-cognito-identity-provider';
import {
  cognitoClient,
  cognitoConfig,
} from '@frontend/features/auth/config/cognitoClient';
import type {
  Folder,
  FolderDocument,
  S3DocumentMetadata,
} from '@frontend/features/documents/types';

export async function getAuthHeaders(): Promise<Record<string, string>> {
  const idToken = localStorage.getItem('cognito_id_token');
  if (idToken) {
    return { Authorization: `Bearer ${idToken}` };
  }
  return {};
}

/** Try to refresh tokens using the stored refresh token. Returns true on success. */
async function tryRefreshTokens(): Promise<boolean> {
  const refreshToken = localStorage.getItem('cognito_refresh_token');
  if (!refreshToken) return false;

  try {
    const response = await cognitoClient.send(
      new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: cognitoConfig.userPoolClientId,
        AuthParameters: { REFRESH_TOKEN: refreshToken },
      }),
    );
    const result = response.AuthenticationResult;
    if (result?.IdToken) {
      localStorage.setItem('cognito_id_token', result.IdToken);
      if (result.AccessToken)
        localStorage.setItem('cognito_access_token', result.AccessToken);
      return true;
    }
  } catch {
    // Refresh token is invalid/expired
  }
  return false;
}

/** Typed fetch wrapper with auth headers and error handling */
async function apiFetch<T>(
  path: string,
  options?: RequestInit & { params?: Record<string, string | undefined> },
): Promise<T> {
  const headers = await getAuthHeaders();
  const url = new URL(`/v1${path}`, window.location.origin);

  if (options?.params) {
    for (const [key, value] of Object.entries(options.params)) {
      if (value !== undefined) url.searchParams.set(key, value);
    }
  }

  let response = await fetch(url.toString(), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
      ...options?.headers,
    },
  });

  // On 401, try refreshing tokens before giving up
  if (response.status === 401) {
    const refreshed = await tryRefreshTokens();
    if (refreshed) {
      const newHeaders = await getAuthHeaders();
      response = await fetch(url.toString(), {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...newHeaders,
          ...options?.headers,
        },
      });
    } else {
      localStorage.removeItem('cognito_id_token');
      localStorage.removeItem('cognito_access_token');
      localStorage.removeItem('cognito_refresh_token');
      window.location.reload();
    }
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(
      (body as { error?: string }).error ?? `API error: ${response.status}`,
    );
  }

  return response.json() as Promise<T>;
}

// ===== Documents =====

export const documentsApi = {
  getPresignedUploadUrl: (data: {
    filename: string;
    fileType: string;
    fileSize: number;
    userId?: string;
  }) =>
    apiFetch<{
      documentId: string;
      s3Key: string;
      uploadUrl: string;
      uploadFields: Record<string, string>;
      expiresIn: number;
    }>('/documents/upload-url', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  delete: (s3Key: string) =>
    apiFetch<{ success: boolean }>(`/documents/${s3Key}`, {
      method: 'DELETE',
    }),

  getPreview: (s3Key: string) =>
    apiFetch<{
      document: S3DocumentMetadata & { uploadDate?: string };
      previewUrl: string;
      downloadUrl: string;
    }>(`/documents/preview/${s3Key}`),

  getIngestionStatus: (params?: { dataSourceId?: string }) =>
    apiFetch<Record<string, unknown>>('/documents/ingestion-status', {
      params,
    }),

  triggerSync: () =>
    apiFetch<{ status: string; jobId?: string }>('/documents/sync', {
      method: 'POST',
    }),
};

// ===== Folders =====

export const foldersApi = {
  list: (params?: { parentId?: string }) =>
    apiFetch<{ folders: Folder[] }>('/folders', { params }),

  create: (data: {
    name: string;
    parentId?: string;
    visibility?: 'public' | 'private';
  }) =>
    apiFetch<Record<string, unknown>>('/folders', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (
    id: string,
    data: {
      name?: string;
      visibility?: 'public' | 'private';
      sharedWith?: string[];
    },
  ) =>
    apiFetch<{ ok: boolean }>(`/folders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiFetch<{ ok: boolean }>(`/folders/${id}`, { method: 'DELETE' }),

  getDocuments: (folderId: string) =>
    apiFetch<{ documents: FolderDocument[] }>(`/folders/${folderId}/documents`),

  createDocument: (
    folderId: string,
    data: {
      documentId: string;
      s3Key: string;
      filename: string;
      fileType: string;
      fileSize: number;
    },
  ) =>
    apiFetch<FolderDocument>(`/folders/${folderId}/documents`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  removeDocument: (folderId: string, documentId: string) =>
    apiFetch<{ ok: boolean }>(`/folders/${folderId}/documents/${documentId}`, {
      method: 'DELETE',
    }),
};

// ===== Bedrock =====

export const bedrockApi = {
  retrieve: (data: { query: string; numberOfResults?: number }) =>
    apiFetch<{ results: unknown[]; nextToken?: string }>('/bedrock/retrieve', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  retrieveAndRerank: (data: {
    query: string;
    numberOfResults?: number;
    minScore?: number;
  }) =>
    apiFetch<{
      results: unknown[];
      totalResults: number;
      filteredResults: number;
    }>('/bedrock/retrieve-and-rerank', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getDocumentChunks: (data: { s3Key: string; maxResults?: number }) =>
    apiFetch<{
      document: S3DocumentMetadata;
      chunks: Array<{
        content?: { text?: string };
        score?: number;
        location?: { s3Location?: { uri?: string } };
        metadata?: Record<string, unknown>;
      }>;
      totalChunks: number;
    }>('/bedrock/chunks', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  generate: (data: {
    query: string;
    modelId?: string;
    numberOfResults?: number;
    temperature?: number;
    maxTokens?: number;
  }) =>
    apiFetch<{
      generatedText: string;
      sessionId: string;
      citations: unknown[];
    }>('/bedrock/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// ===== Users =====

export interface UserProfile {
  email: string;
  role: 'admin' | 'user';
  status: string;
  createdAt: string;
}

export const usersApi = {
  me: () =>
    apiFetch<{ email: string; userId: string; role: 'admin' | 'user' }>(
      '/users/me',
    ),

  list: () => apiFetch<{ users: UserProfile[] }>('/users'),

  create: (data: { email: string; role?: 'admin' | 'user' }) =>
    apiFetch<{ email: string; role: string }>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateRole: (email: string, role: 'admin' | 'user') =>
    apiFetch<{ ok: boolean }>(`/users/${encodeURIComponent(email)}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),

  delete: (email: string) =>
    apiFetch<{ ok: boolean }>(`/users/${encodeURIComponent(email)}`, {
      method: 'DELETE',
    }),
};

// ===== Audit Logs =====

export interface AuditLogEntry {
  id: string;
  action: 'user.create' | 'user.delete' | 'user.role_change';
  performedBy: string;
  targetEmail: string;
  details: Record<string, unknown>;
  createdAt: string;
}

export const auditLogsApi = {
  list: (params?: { startDate?: string; endDate?: string; limit?: string }) =>
    apiFetch<{ logs: AuditLogEntry[]; cursor?: unknown }>('/audit-logs', {
      params,
    }),
};

// ===== Chat =====

export const chatApi = {
  listConversations: () =>
    apiFetch<{ conversations: unknown[]; cursor?: unknown }>(
      '/chat/conversations',
    ),

  createConversation: (data?: { title?: string }) =>
    apiFetch<Record<string, unknown>>('/chat/conversations', {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    }),

  deleteConversation: (id: string) =>
    apiFetch<{ ok: boolean }>(`/chat/conversations/${id}`, {
      method: 'DELETE',
    }),

  getMessages: (conversationId: string, params?: { limit?: string }) =>
    apiFetch<{ messages: unknown[]; cursor?: unknown }>(
      `/chat/conversations/${conversationId}/messages`,
      { params },
    ),
};
